import React from 'react';

import { S3_BUCKET_URL } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { WebCampaignType } from '@/global';
import { useAccount } from '@/hooks/useAccount.ts';
import { useBrowserContentColor } from '@/hooks/useBrowserContentColor';
import { amplitudeModule } from '@/modules/amplitude';
import { AxonModule } from '@/modules/axon.ts';
import { queryParamsModule } from '@/modules/queryParams';
import { webCampaign } from '@/modules/webCampaign';
import { canLogEvents } from '@/utils/canLogEvents';

import PaywallDefaultShort from './Variants/DefaultShort/DefaultShort';
import PaywallDevmode from './Variants/Devmode/Devmode';
import FastPayTabs from './Variants/FastPayTabs/FastPayTabs';
import FastPayTabsDominant from './Variants/FastPayTabsDominant/FastPayTabsDominant';
import PaywallFastPayTabsMan from './Variants/FastPayTabsMan/FastPayTabsMan';
import PaywallFastPayTabsMultigender from './Variants/FastPayTabsMultigender/FastPayTabsMultigender';
import FastPayTabsRoleplay from './Variants/FastPayTabsRoleplay/FastPayTabsRoleplay';
import PaywallV1 from './Variants/V1/PaywallV1';
import PaywallV2 from './Variants/V2/PaywallV2';
import PaywallV6 from './Variants/V6/PaywallV6';

const PaywallVariantMap: Record<
  NonNullable<WebCampaignType['paywall']>,
  React.FC
> = {
  // Dev paywalls
  devmode: PaywallDevmode,
  // Production paywalls
  v1: PaywallV1,
  v2: PaywallV2,
  v6: PaywallV6,
  'fast-pay-tabs': FastPayTabs,
  'fast-pay-tabs-domination': FastPayTabsDominant,
  'fast-pay-tabs-roleplay': FastPayTabsRoleplay,
  'default-short': PaywallDefaultShort,
  'fast-pay-tabs-man': PaywallFastPayTabsMan,
  'fast-pay-tabs-multigender': PaywallFastPayTabsMultigender,
};

const preloadS3Images = () => {
  const paywallNotes = webCampaign.setup.paywallNotes;

  if (paywallNotes.includes(PAYWALL_NOTES.SHOW_COINS_PAYWALL_MODAL)) {
    const coinsImages = [
      `${S3_BUCKET_URL}/modal-coins-upsale/female.png`,
      `${S3_BUCKET_URL}/modal-coins-upsale/male.png`,
    ];

    if (paywallNotes.includes(PAYWALL_NOTES.COINS_UPSALE_V1)) {
      coinsImages.push(
        `${S3_BUCKET_URL}/modal-coins-upsale/v1/female.png`,
        `${S3_BUCKET_URL}/modal-coins-upsale/v1/male.png`,
      );
    }
    if (paywallNotes.includes(PAYWALL_NOTES.COINS_UPSALE_V2)) {
      coinsImages.push(
        `${S3_BUCKET_URL}/modal-coins-upsale/v2/female.png`,
        `${S3_BUCKET_URL}/modal-coins-upsale/v2/male.png`,
      );
    }
    if (paywallNotes.includes(PAYWALL_NOTES.COINS_UPSALE_V3)) {
      coinsImages.push(
        `${S3_BUCKET_URL}/modal-coins-upsale/v3/female.png`,
        `${S3_BUCKET_URL}/modal-coins-upsale/v3/male.png`,
      );
    }

    coinsImages.push(`${S3_BUCKET_URL}/icons/coins-amount-huge.png`);

    coinsImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }
};

const OnboardingPaywall: React.FC = () => {
  const { user } = useAccount();
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Paywall',
      });
      AxonModule.trackPageView(user?.id);
    }

    queryParamsModule.removeParam('step');

    // Preload S3 images for better performance
    preloadS3Images();
  }, []);

  useBrowserContentColor();

  const PaywallVariant =
    PaywallVariantMap[webCampaign.setup.paywall || 'default-short'];

  return <PaywallVariant />;
};

export default OnboardingPaywall;
